import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { ChangeEvent, useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuditoriasLista } from "../../domain/models/auditorias_lista";
import { useAuditoriasFilters } from "../hooks/Auditoria_auditorias/filter";
import { useAuditoriasPagination } from "../hooks/Auditoria_auditorias/pagination";
import {
  AuditoriaAauditoriaForm,
  AuditoriaAauditoriaSchema,
} from "../schemas/Auditoria_auditoria/auditoria_auditoria";
import {
  AuditoriaProcessoAauditoriaForm,
  AuditoriaProcessoAauditoriaSchema,
} from "../schemas/Auditoria_auditoria/auditoria_processo_auditoria";
import { ProcessoAuditoriasLista } from "../../domain/models/processo_auditorias_lista";
import { useProcessoAuditoriasPagination } from "../hooks/Auditoria_auditorias/processo_auditoria_pagination";
import {
  defaultReportPlanoAuditoriaValues,
  ReportPlanoAuditoriaForm,
  ReportPlanoAuditoriaSchema,
} from "../schemas/Auditoria_auditoria/report_plano_treinamento";
import { useNavigate, useParams } from "react-router";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { isValidFileName } from "../../../../common/utils/isValidFileName";

type LoadAuditoriasListaFuncState = {
  IdAuditoria?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number;
  status?: number;
  pesquisa?: string | number | Date;
};
type LoadProcessoAuditoriasListaFuncState = {
  IdAuditoria?: number;
  page?: number;
  rowsPerPage?: number;
};

export const useAuditoriasCadastroViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
      getCentroCustoDropDownUseCase,
    },
    auditor: {
      getAllListagemAuditoriaUseCase,
      getAllListagemProcessoAuditoriaUseCase,
      createAuditoriaUseCase,
      createProcessoAuditoriaUseCase,
      updateAuditoriaUseCase,
      updateProcessoAuditoriaUseCase,
      deleteProcessoAuditoriaUseCase,
      copiaProcessoAuditoriaUseCase,
      getArquivoAuditoriaUseCase,
    },
  } = useDependencyInjection();

  const { id } = useParams();
  const navigate = useNavigate();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const hookForm = useForm<AuditoriaAauditoriaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AuditoriaAauditoriaSchema),
    defaultValues: {
      TipoAuditoria: 0,
      EmAberto: 1,
      DataElaboracao: new Date(),
      DataAuditoria: new Date(),
    },
  });

  const hookFormReport = useForm<ReportPlanoAuditoriaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultReportPlanoAuditoriaValues,
    resolver: zodResolver(ReportPlanoAuditoriaSchema),
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [AuditoriasModal, setAuditoriasModal] = useState<boolean>(false);
  const [processoAuditoriasLista, setProcessoAuditoriasLista] = useState<
    ProcessoAuditoriasLista[]
  >([]);
  const [selectedAuditoria, setSelectedAuditoria] = useState<
    AuditoriasLista | undefined
  >(undefined);
  const [selectedProcessoAuditoria, setSelectedProcessoAuditoria] = useState<
    ProcessoAuditoriasLista | undefined
  >(undefined);
  const [centroCustoDropdown, setCentroCustoDropdown] =
    useState<CentroCustoDropDown[]>();

  const [idAuditoria, setIdAuditoria] = useState<number>(0);

  //Arquivo
  const [AuditoriasDeleteModal, setAuditoriasDeleteModal] =
    useState<boolean>(false);
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] =
    useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();

  const {
    pushErrorNotification,
    pushWarnNotification,
    pushInfoNotification,
    setCustomLoading,
  } = usePageLoader();

  const filters = useAuditoriasFilters();
  const pagination = useAuditoriasPagination();
  const paginationProcess = useProcessoAuditoriasPagination();

  //#region Programacao
  const [openProcesso, setOpenProcesso] = useState<boolean>(false);
  const [openProcessoDelete, setOpenProcessoDelete] = useState<boolean>(false);
  const [selectProcesso, setSelectProcesso] =
    useState<ProcessoAuditoriasLista>();
  const hookFormProcesso = useForm<AuditoriaProcessoAauditoriaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(AuditoriaProcessoAauditoriaSchema),
    defaultValues: {
      ResponsavelCadastrado: false,
    },
  });
  //#endregion

  //#region Copia
  const [openCopiaModal, setOpenCopiaModal] = useState<boolean>(false);
  //#endregion

  // #region Funções de carregamento

  const loadAuditoriasListaState = useCallback(
    async (funcState?: LoadAuditoriasListaFuncState) => {
      try {
        const filterID_Auditoria = funcState?.IdAuditoria ?? null;
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllListagemAuditoriaUseCase.getAllListagemAuditoria(
          {
            IdAuditoria: filterID_Auditoria,
          },
          filteredPage,
          filteredRowsPerPage,
        );
        pagination.setRowsPerPage(getAllListagemAuditoriaUseCase.pageSize);
        pagination.setTotalItems(getAllListagemAuditoriaUseCase.totalItems);

        const auditoria =
          getAllListagemAuditoriaUseCase.items &&
          getAllListagemAuditoriaUseCase.items.length > 0
            ? getAllListagemAuditoriaUseCase.items[0]
            : null;
        if (auditoria) {
          setSelectedAuditoria(auditoria);
          hookForm.setValue("Evento", auditoria.Evento);

          let emAberto = auditoria.EmAberto ? 1 : 0;
          hookForm.setValue("EmAberto", emAberto);

          if (auditoria.DataAuditoria)
            hookForm.setValue(
              "DataAuditoria",
              new Date(auditoria.DataAuditoria),
            );

          if (auditoria.DataElaboracao)
            hookForm.setValue(
              "DataElaboracao",
              new Date(auditoria.DataElaboracao),
            );

          if (auditoria.DataTermino)
            hookForm.setValue("DataTermino", new Date(auditoria.DataTermino));

          hookForm.setValue("Criterio", auditoria.Criterio);
          hookForm.setValue("IdAuditor1", auditoria.IdAuditor1);
          hookForm.setValue("IdAuditor2", auditoria.IdAuditor2);
          hookForm.setValue("IdAuditor3", auditoria.IdAuditor3);
          hookForm.setValue("Observacao", auditoria.Observacao);
          hookForm.setValue("TipoAuditoria", auditoria.TipoAuditoria);
          hookForm.setValue("nomeArquivo1", auditoria.nomeArquivo1);
          hookForm.setValue("nomeArquivo2", auditoria.nomeArquivo2);
        } else {
          setSelectedAuditoria(undefined);
          hookForm.reset({ EmAberto: 1, TipoAuditoria: 1 });
        }
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Auditorias!");
      }
    },
    [getAllListagemAuditoriaUseCase, hookForm, pagination],
  );

  const loadProcessoAuditoriasListaState = useCallback(
    async (funcState?: LoadProcessoAuditoriasListaFuncState) => {
      try {
        const filterID_Auditoria = funcState?.IdAuditoria ?? null;
        const filteredPage = funcState?.page ?? paginationProcess.page;
        const filteredRowsPerPage =
          funcState?.rowsPerPage ?? paginationProcess.rowsPerPage;
        await getAllListagemProcessoAuditoriaUseCase.getAllListagemProcessoAuditoria(
          {
            IdAuditoria: filterID_Auditoria,
          },
          filteredPage,
          filteredRowsPerPage,
        );
        setProcessoAuditoriasLista(
          getAllListagemProcessoAuditoriaUseCase.items,
        );
        paginationProcess.setRowsPerPage(
          getAllListagemProcessoAuditoriaUseCase.pageSize,
        );
        paginationProcess.setTotalItems(
          getAllListagemProcessoAuditoriaUseCase.totalItems,
        );
      } catch (e: unknown) {
        throw new Error("Falha ao buscar Processo Auditorias!");
      }
    },
    [getAllListagemProcessoAuditoriaUseCase, paginationProcess],
  );

  const loadOrdemCompraCentroCustoDropDown = useCallback(
    async (id?: number) => {
      try {
        const results =
          await getCentroCustoDropDownUseCase.getCentroCustoDropDown(undefined);
        setCentroCustoDropdown(results);
      } catch (e: unknown) {
        setCentroCustoDropdown(undefined);
        throw new Error("Falha ao buscar centro custo!");
      }
    },
    [getCentroCustoDropDownUseCase],
  );
  //#endregion

  // #region useEffect
  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      if (id) {
        setIdAuditoria(Number(id));
        jobs.push(loadAuditoriasListaState({ IdAuditoria: Number(id) }));
        jobs.push(
          loadProcessoAuditoriasListaState({ IdAuditoria: Number(id) }),
        );
      }
      jobs.push(loadOrdemCompraCentroCustoDropDown());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      if (id) {
        setIdAuditoria(Number(id));
        jobs.push(loadAuditoriasListaState({ IdAuditoria: Number(id) }));
        jobs.push(
          loadProcessoAuditoriasListaState({ IdAuditoria: Number(id) }),
        );
      }
      jobs.push(loadOrdemCompraCentroCustoDropDown());

      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
        { isAutoFinish: true },
      );
    } finally {
      setLoading(false);
    }
  }, [
    id,
    loadAuditoriasListaState,
    loadOrdemCompraCentroCustoDropDown,
    loadProcessoAuditoriasListaState,
    pushErrorNotification,
  ]);
  //#endregion

  // #region Eventos

  const handleTableProcessChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        paginationProcess.setPage(page + 1);
        await loadProcessoAuditoriasListaState({
          IdAuditoria: idAuditoria,
          page: page + 1,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      idAuditoria,
      loadProcessoAuditoriasListaState,
      paginationProcess,
      pushErrorNotification,
    ],
  );

  const handleRowsPerTableProcessChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        paginationProcess.setPage(1);
        paginationProcess.setRowsPerPage(rowsPerPage);

        await loadProcessoAuditoriasListaState({
          IdAuditoria: idAuditoria,
          page: 1,
          rowsPerPage,
        });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
          { isAutoFinish: true },
        );
      } finally {
        setLoading(false);
      }
    },
    [
      idAuditoria,
      loadProcessoAuditoriasListaState,
      paginationProcess,
      pushErrorNotification,
    ],
  );

  const handleAuditorialSubmit = useCallback(
    async (form: AuditoriaAauditoriaForm) => {
      try {
        setCustomLoading(true);
        setSaving(true);

        let emAberto = form.EmAberto === 1 ? true : false;

        if (selectedAuditoria) {
          await updateAuditoriaUseCase.updateAuditoria(
            selectedAuditoria.IdAuditoria,
            form.Evento ?? null,
            form.TipoAuditoria ?? null,
            emAberto ?? null,
            form.DataElaboracao ?? null,
            form.DataAuditoria ?? null,
            form.DataTermino ?? null,
            form.Criterio ?? null,
            form.Observacao ?? null,
            form.IdAuditor1 ?? null,
            form.IdAuditor2 ?? null,
            form.IdAuditor3 ?? null,
            form.Arquivo1 ?? null,
            form.Arquivo2 ?? null,
            form.nomeArquivo1 ?? null,
            form.nomeArquivo2 ?? null,
            form.limparArquivo1 ?? false,
            form.limparArquivo2 ?? false,
          );
          pushInfoNotification("Sucesso", "Auditoria atualizado com sucesso!", {
            isAutoFinish: true,
          });
          setAuditoriasModal(false);
          await loadAuditoriasListaState({
            IdAuditoria: selectedAuditoria.IdAuditoria,
          });
        } else {
          var idAuditoria = await createAuditoriaUseCase.createAuditoria(
            form.Evento ?? null,
            form.TipoAuditoria ?? null,
            emAberto,
            form.DataElaboracao ?? null,
            form.DataAuditoria ?? null,
            form.DataTermino ?? null,
            form.Criterio ?? null,
            form.Observacao ?? null,
            form.IdAuditor1 ?? null,
            form.IdAuditor2 ?? null,
            form.IdAuditor3 ?? null,
            form.Arquivo1 ?? null,
            form.Arquivo2 ?? null,
            form.nomeArquivo1 ?? null,
            form.nomeArquivo2 ?? null,
          );
          pushInfoNotification("Sucesso", "Auditoria criado com sucesso!", {
            isAutoFinish: true,
          });
          setIdAuditoria(idAuditoria);
          await loadAuditoriasListaState({ IdAuditoria: idAuditoria });
        }
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar/atualizar Auditoria!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
        setCustomLoading(false);
      }
    },
    [
      createAuditoriaUseCase,
      loadAuditoriasListaState,
      pushErrorNotification,
      pushInfoNotification,
      selectedAuditoria,
      setCustomLoading,
      updateAuditoriaUseCase,
    ],
  );

  const handleProcesso = useCallback(async () => {
    if (idAuditoria > 0 || selectedAuditoria) {
      setOpenProcessoDelete(false);
      setOpenProcesso(true);
      setSelectProcesso(undefined);
      let id = idAuditoria > 0 ? idAuditoria : selectedAuditoria?.IdAuditoria;
      hookFormProcesso.reset({
        DescResponsavel: "",
        Descricao: "",
        Horario: null,
        IdAuditoria: Number(id),
        RequisitoNormativo: "",
        FuncionarioNome: "",
        IdFuncionario: null,
        IdProcesso: null,
        Data: new Date(),
      });
      setLoading(false);
    } else {
      setOpenProcessoDelete(false);
      setSelectProcesso(undefined);
      setOpenProcesso(false);
      return pushWarnNotification(
        "Atenção",
        "Para incluir uma programação é necessário salvar o cadastro.",
      );
    }
  }, [hookFormProcesso, idAuditoria, pushWarnNotification, selectedAuditoria]);

  const handleEdicaoProcessoOpenState = useCallback(
    (IdProcesso: number | null, openState: boolean) => {
      if (openState) {
        setOpenProcesso(true);
        if (IdProcesso) {
          const itemParaEditar = processoAuditoriasLista.find(
            (x) => x.IdProcesso === IdProcesso,
          );
          if (itemParaEditar) {
            setSelectProcesso(itemParaEditar);
          } else {
            setSelectProcesso(undefined);
            hookFormProcesso.reset();
          }
        } else {
          setSelectProcesso(undefined);
          hookFormProcesso.reset();
        }
      }
    },
    [processoAuditoriasLista, hookFormProcesso],
  );

  const handleProcessoAuditorialSubmit = useCallback(
    async (form: AuditoriaProcessoAauditoriaForm) => {
      try {
        setSaving(true);
        if (form.IdProcesso && form.IdAuditoria) {
          await updateProcessoAuditoriaUseCase.updateProcessoAuditoria(
            form.IdProcesso,
            form.IdAuditoria,
            form.Descricao ?? null,
            form.IdFuncionario ?? null,
            form.DescResponsavel ?? null,
            form.ResponsavelCadastrado ?? null,
            form.Data ?? null,
            form.Horario ?? null,
            form.RequisitoNormativo ?? null,
            form.Local ?? null,
            
          );

          pushInfoNotification(
            "Sucesso",
            "Programação atualizado com sucesso!",
            { isAutoFinish: true },
          );
        } else {
          await createProcessoAuditoriaUseCase.createProcessoAuditoria(
            form.IdAuditoria,
            form.Descricao ?? null,
            form.IdFuncionario ?? null,
            form.DescResponsavel ?? null,
            form.ResponsavelCadastrado ?? null,
            form.Data ?? null,
            form.Horario ?? null,
            form.RequisitoNormativo ?? null,
            form.Local ?? null,
          );

          pushInfoNotification("Sucesso", "Programação criado com sucesso!", {
            isAutoFinish: true,
          });
        }
        setOpenProcesso(false);
        await loadProcessoAuditoriasListaState({
          IdAuditoria: form.IdAuditoria,
        });
      } catch (e) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao criar Auditoria!"),
          { isAutoFinish: true },
        );
      } finally {
        setSaving(false);
      }
    },
    [
      createProcessoAuditoriaUseCase,
      loadProcessoAuditoriasListaState,
      pushErrorNotification,
      pushInfoNotification,
      updateProcessoAuditoriaUseCase,
    ],
  );

  const handleDeleteProcessoModalOpenState = useCallback(
    (IdProcesso: number | null, openState: boolean) => {
      if (openState) {
        if (IdProcesso && processoAuditoriasLista) {
          setSelectProcesso(
            processoAuditoriasLista.find((x) => x.IdProcesso === IdProcesso),
          );
        } else {
          setSelectProcesso(undefined);
        }
      }
      setOpenProcessoDelete(openState);
    },
    [processoAuditoriasLista],
  );

  const handleDeleteConfirmaProcessoAuditoria = useCallback(async () => {
    try {
      if (selectProcesso && Number(selectProcesso.IdProcesso) > 0) {
        setSaving(true);
        await deleteProcessoAuditoriaUseCase.deleteProcessoAuditoria(
          selectProcesso.IdProcesso,
          selectProcesso.IdAuditoria,
        );
        await loadProcessoAuditoriasListaState({
          IdAuditoria: selectProcesso?.IdAuditoria,
        });
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao excluir informações!"),
        { isAutoFinish: true },
      );
    } finally {
      setOpenProcessoDelete(false);
      setSaving(false);
    }
  }, [
    deleteProcessoAuditoriaUseCase,
    loadProcessoAuditoriasListaState,
    pushErrorNotification,
    selectProcesso,
  ]);

  //#endregion

  //#region ARQUIVOS
  const handleDownload = useCallback(
    async (filename: string, imagePosition: number) => {
      if (!saving) {
        try {
          setCustomLoading(true);
          await getArquivoAuditoriaUseCase.getArquivoAuditoria(
            Number(id),
            imagePosition,
            filename,
          );
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao salvar inspeção obra!"),
          );
        } finally {
          setCustomLoading(false);
          setSaving(false);
        }
      }
    },
    [
      getArquivoAuditoriaUseCase,
      id,
      pushErrorNotification,
      saving,
      setCustomLoading,
    ],
  );
  const handleArquivoDeleteOpenState = useCallback(
    (
      limparArquivos: string | null,
      arquivo: string | null,
      openState: boolean,
    ) => {
      if (openState && limparArquivos && arquivo) {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(limparArquivos);
        setArquivo(arquivo);
      } else {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(undefined);
        setArquivo(undefined);
      }
    },
    [],
  );

  // const handleFileChange = useCallback(
  //   (
  //     fileId: number | null
  //   ) => {
  //     if (fileId && selectedAuditoria) {
  //       switch (fileId) {
  //         case 1:
  //           setSelectedAuditoria((prevState) => {
  //             if (!prevState) return prevState; 
  //             return {
  //               ...prevState,
  //               nomeArquivo1: "",
  //             };
  //           });
  //           hookForm.setValue("limparArquivo1", true);
  //           break;

  //         case 2:
  //           setSelectedAuditoria((prevState) => {
  //             if (!prevState) return prevState; 
  //             return {
  //               ...prevState,
  //               nomeArquivo2: "",
  //             };
  //           });
  //           hookForm.setValue("limparArquivo2", true);
  //           break;

  //         default:
  //           break;
  //       }
  //       // hookForm.setValue(limparArquivos as any, false);
  //       // hookForm.setValue(
  //       //   nomeArquivo as any,
  //       //   e.target.files?.item(0)?.name ?? "",
  //       // );
  //       // onChange(e.target.files?.item(0));
  //     }
  //   },
  //   [selectedAuditoria, hookForm],
  // );

  const handleDeleteFile = useCallback(
    (limparArquivos: string, arquivo: string) => {
      if (!saving) {
        hookForm.setValue(limparArquivos as any, true);
        hookForm.setValue(arquivo as any, null);
        setSelectedAuditoria((prev) =>
          prev ? { ...prev, [arquivo]: null } : undefined,
        );
        if (limparArquivo === "limparArquivo1") hookForm.setValue("limparArquivo1", true);
        else hookForm.setValue("limparArquivo2", true);
      }
    },
    [saving, hookForm, limparArquivo],
  );

  const handleConfirmarDeletarArquivo = useCallback(async () => {
    if (limparArquivo && arquivo) {
      handleDeleteFile(limparArquivo, arquivo);
      setOpenDeleteArquivoModal(false);
      setArquivo(undefined);
      setLimparArquivo(undefined);
    }
  }, [limparArquivo, arquivo, handleDeleteFile]);
  //#endregion

  const handleVoltarOpenState = useCallback(() => {
    navigate(`/Auditorias_lista/`);
  }, [navigate]);

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (idAuditoria) {
          changeHistory.handleOpenChangeHistory(
            idAuditoria,
            LogTipoTabela.AUDITORIA,
            true,
          );
        }
      }
    },
    [idAuditoria, changeHistory],
  );

  //#region
  const handleProcessoAuditoriaCopiaOpenState = useCallback(
    (IdProcesso: number | null, openState: boolean) => {
      if (openState) {
        setSelectedProcessoAuditoria(
          processoAuditoriasLista.find((x) => x.IdProcesso === IdProcesso),
        );
        setOpenCopiaModal(true);
      } else {
        setOpenCopiaModal(false);
      }
    },
    [processoAuditoriasLista],
  );
  const handleSubmitCopiaProcessoAuditoria = useCallback(
    async (form: AuditoriaProcessoAauditoriaForm) => {
      try {
        if (selectedAuditoria) {
          setSaving(true);
          setLoading(true);
          await copiaProcessoAuditoriaUseCase.copiaProcessoAuditoria(
            form.IdProcesso ?? 0,
            form.IdAuditoria,
            form.Descricao,
            form.IdFuncionario ?? null,
            form.DescResponsavel ?? null,
            form.ResponsavelCadastrado ?? null,
            form.Data,
            form.Horario,
            form.RequisitoNormativo ?? null,
            form.Local ?? null,
          );
          await loadProcessoAuditoriasListaState({
            IdAuditoria: selectedAuditoria.IdAuditoria,
          });
          pushInfoNotification(
            "Sucesso",
            "Processo de auditoria copiado com sucesso!",
            { isAutoFinish: true },
          );
        }
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao copiar Processo de Auditoria!"),
        );
      } finally {
        setOpenCopiaModal(false);
        setSaving(false);
        setLoading(false);
      }
    },
    [
      selectedAuditoria,
      copiaProcessoAuditoriaUseCase,
      loadProcessoAuditoriasListaState,
      pushInfoNotification,
      pushErrorNotification,
    ],
  );
  //#endregion

  return {
    loading,
    saving,
    errorMessage,
    AuditoriasModal,
    AuditoriasDeleteModal,
    openDeleteArquivoModal,
    pagination,
    filters,
    selectedAuditoria,
    idAuditoria,
    processoAuditoriasLista,
    paginationProcess,
    hookFormReport,
    hookForm,
    openProcesso,
    hookFormProcesso,
    selectProcesso,
    openProcessoDelete,
    changeHistory,
    centroCustoDropdown: centroCustoDropdown ?? [],
    openCopiaModal,
    selectedProcessoAuditoria,
    handleOpenChangeHistory,
    setOpenProcessoDelete,
    handleDeleteProcessoModalOpenState,
    handleEdicaoProcessoOpenState,
    setOpenProcesso,
    handleProcesso,
    handleVoltarOpenState,
    handleReload,
    handleAuditorialSubmit,
    handleProcessoAuditorialSubmit,
    handleTableProcessChange,
    handleRowsPerTableProcessChange,
    handleArquivoDeleteOpenState,
    handleDownload,
    // handleFileChange,
    handleConfirmarDeletarArquivo,
    handleDeleteConfirmaProcessoAuditoria,
    handleSubmitCopiaProcessoAuditoria,
    handleProcessoAuditoriaCopiaOpenState,
  };
};
